import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "gf_theme_mode";
const LEGACY_KEY = "gf_theme"; // pre-Feb 23 2026 — plain "light" / "dark" only

/**
 * Theme provider.
 *
 * Two concepts:
 *   - `mode`  — the user's PREFERENCE: "light" | "dark" | "system".
 *   - `theme` — the RESOLVED value actually applied to the DOM:
 *               "light" | "dark". If mode === "system" this follows the OS
 *               `prefers-color-scheme` media query in real time.
 *
 * We keep the resolved theme mirrored to `document.documentElement[data-theme]`
 * so the existing CSS custom-properties keep working exactly as before.
 * `toggle` is retained for back-compat but now cycles Light → Dark → System.
 */

const _resolve = (mode) => {
  if (mode === "system") {
    try {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch { return "light"; }
  }
  return mode === "dark" ? "dark" : "light";
};

const _loadInitialMode = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
    // Migrate from the legacy "gf_theme" key which only stored light/dark.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") return legacy;
  } catch { /* ignore */ }
  return "system";
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(_loadInitialMode);
  const [theme, setResolved] = useState(() => _resolve(mode));

  // Persist mode + apply resolved theme to <html>.
  useEffect(() => {
    const resolved = _resolve(mode);
    setResolved(resolved);
    document.documentElement.setAttribute("data-theme", resolved);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  // React to OS-level theme changes when the user picked "system".
  useEffect(() => {
    if (mode !== "system") return undefined;
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return undefined; }
    const onChange = () => {
      const resolved = _resolve("system");
      setResolved(resolved);
      document.documentElement.setAttribute("data-theme", resolved);
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [mode]);

  const setMode = useCallback((next) => {
    if (next !== "light" && next !== "dark" && next !== "system") return;
    setModeState(next);
  }, []);

  // Back-compat: `toggle` cycles Light → Dark → System → Light. Kept so any
  // remaining legacy callers keep working, but the Header no longer uses it.
  const toggle = useCallback(() => {
    setModeState((m) => (m === "light" ? "dark" : m === "dark" ? "system" : "light"));
  }, []);

  // Back-compat alias: `setTheme("light" | "dark")` still works; anything else
  // is coerced to "system".
  const setTheme = useCallback((v) => {
    setMode(v === "light" || v === "dark" ? v : "system");
  }, [setMode]);

  return (
    <ThemeContext.Provider value={{ mode, theme, setMode, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
